/**
 * Settings -> Labels: the vocabulary the mail desk and the call desk tag with.
 *
 * Two independent lists on one page, each saved by the right that owns its desk
 * (`manage:crm.email`, `manage:crm.calls`). A staff member who holds only one of
 * them edits only that list and reads the other, rather than being offered a
 * save button that answers 403 - the read-only list drops the inputs entirely
 * and renders as an ordinary CRM list, which is a truer picture than a row of
 * greyed-out form controls.
 *
 * Renaming is safe and the copy says so: a label keeps the id it was created
 * with, so the threads and calls carrying it follow the new name.
 *
 * Removing a row takes effect only on Save, and even then it does not touch a
 * single thread or call - it takes the label out of the registry, so the rows
 * that carried it simply stop drawing a chip. That also means re-adding a label
 * under the same name brings it back on exactly those rows, because the id is
 * derived from the name. No ConfirmModal for that reason: nothing here is
 * destroyed, and a warning on a reversible act teaches people to click through
 * warnings.
 */
import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Loader2, MoreHorizontal, Plus, RefreshCw, Save, Tags, Trash2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import { Pop, PopHost, useClosePops } from "../../shell/Pop";
import { Btn, Card, CardHead, EmptyState, LabelChips, MenuField, listCard } from "../../ui";
import {
  LABEL_TONES,
  MAX_LABELS,
  getCrmLabels,
  labelColor,
  saveCrmLabels,
  type CrmLabel,
  type CrmLabelSets,
  type LabelScope,
  type LabelTone,
} from "../../../lib/crmLabels";

/**
 * Name, colour, preview, actions.
 *
 * The name track carries a text field and the colour track a menu whose trigger
 * asks for 168px of its own, so both are given a pixel minimum instead of the
 * bare `fr` the browse lists use - `listCard` reads those minima back out to
 * work out where the card should start scrolling rather than squeezing.
 */
const COLS = "minmax(200px,1.8fr) minmax(176px,1fr) minmax(0,1.2fr) 44px";

/** A row being edited. `id` is absent on one the operator has just added - that
 *  is how the server knows to mint a slug instead of keeping an existing one. */
interface Draft {
  id?: string;
  name: string;
  tone: LabelTone;
  /** Stable React key. The id cannot serve: a new row has none yet. */
  key: string;
}

/** `saved:` prefixes the key so a stored label whose slug happens to be "new"
 *  cannot collide with the `new-N` keys the add button hands out. */
function toDraft(label: CrmLabel, i: number): Draft {
  return { id: label.id, name: label.name, tone: label.tone, key: `saved-${i}-${label.id}` };
}

/** The 44px cell at the end of a row. One entry, because there is one thing to
 *  do to a label from the list. */
function LabelRowMenu({ onRemove }: { onRemove: () => void }) {
  const t = useT();
  const close = useClosePops();
  return (
    <span className="relative z-[1] justify-self-end">
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={cn("crm-cb", open && "is-on")}
            title={t("common.more")}
            aria-label={t("common.more")}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <MoreHorizontal className="size-4" />
          </button>
        )}
      >
        <button
          type="button"
          className="crm-mi crm-mi-danger"
          onClick={() => {
            close();
            onRemove();
          }}
        >
          <Trash2 className="size-4" />
          {t("settings.labels.remove")}
        </button>
      </Pop>
    </span>
  );
}

function LabelRow({
  row,
  canEdit,
  onPatch,
  onRemove,
}: {
  row: Draft;
  canEdit: boolean;
  onPatch: (next: Partial<Draft>) => void;
  onRemove: () => void;
}) {
  const t = useT();
  const name = row.name.trim();
  /**
   * The chip exactly as a thread will draw it. `LabelChips` resolves ids
   * against a registry, so the row hands it a one-entry registry of itself -
   * which is also why the row key stands in for the id a new label has not been
   * given yet.
   */
  const preview: CrmLabel = { id: row.key, name, tone: row.tone };

  return (
    // Soft corners rather than the 999px pill: this row holds a 44px field and a
    // menu trigger, and a full pill would cut into both ends of them.
    <div className="crm-list-row crm-list-row-soft" style={{ gridTemplateColumns: COLS }}>
      {canEdit ? (
        <label className="crm-inp h-11 min-w-0">
          <input
            value={row.name}
            maxLength={32}
            placeholder={t("settings.labels.namePlaceholder")}
            aria-label={t("settings.labels.col.name")}
            onChange={(e) => onPatch({ name: e.target.value })}
          />
        </label>
      ) : (
        <span className="crm-prow-n crm-trunc">{name || "-"}</span>
      )}

      {/* Named, not just coloured: "primary2" is a token, and a dot on its own is
          not something a colour-blind operator can pick from. The menu shows the
          swatch beside the colour's name in both the trigger and every entry. */}
      {canEdit ? (
        <MenuField
          value={row.tone}
          onChange={(tone) => onPatch({ tone })}
          label={t("settings.labels.col.tone")}
          options={LABEL_TONES.map((tone) => ({
            value: tone,
            label: t(`settings.labels.tone.${tone}`),
            icon: <i className="crm-lbl-dot" style={{ background: labelColor(tone) }} />,
          }))}
        />
      ) : (
        <span className="crm-mut flex min-w-0 items-center gap-2 text-[12.5px]">
          <i className="crm-lbl-dot" style={{ background: labelColor(row.tone) }} />
          <span className="crm-trunc">{t(`settings.labels.tone.${row.tone}`)}</span>
        </span>
      )}

      <span className="min-w-0">
        {name ? <LabelChips labels={[preview]} tags={[preview.id]} /> : <span className="crm-mut text-[12.5px]">-</span>}
      </span>

      {canEdit ? <LabelRowMenu onRemove={onRemove} /> : <span />}
    </div>
  );
}

function LabelList({
  scope,
  labels,
  canEdit,
  onSaved,
}: {
  scope: LabelScope;
  labels: CrmLabel[];
  canEdit: boolean;
  onSaved: (next: CrmLabel[]) => void;
}) {
  const t = useT();
  const [rows, setRows] = useState<Draft[]>(() => labels.map(toDraft));
  const [state, setState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  /**
   * Keys for rows that have no id yet, from a counter that only goes up.
   *
   * Deriving one from the row count collides: add a row (`new-3`), delete an
   * earlier one, add another - and the second new row is `new-3` too. React
   * then reuses the first row's input for it, and the name the operator just
   * typed jumps to the wrong line.
   */
  const nextKey = useRef(0);

  // The lists arrive after the first paint, and a save elsewhere on the page
  // rewrites them. Both have to land in the form.
  useEffect(() => setRows(labels.map(toDraft)), [labels]);

  const patch = (key: string, next: Partial<Draft>) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...next } : r)));

  const addRow = () => {
    const key = `new-${(nextKey.current += 1)}`;
    setRows((prev) => [...prev, { name: "", tone: "neutral", key }]);
    setState("idle");
  };

  async function save() {
    setState("saving");
    try {
      const res = await saveCrmLabels(
        scope,
        rows.filter((r) => r.name.trim()).map((r) => ({ id: r.id, name: r.name.trim(), tone: r.tone })),
      );
      onSaved(res.labels);
      setState("saved");
      setTimeout(() => setState("idle"), 2500);
    } catch {
      setState("error");
    }
  }

  const addBtn = (
    <Btn small onClick={addRow} disabled={rows.length >= MAX_LABELS}>
      <Plus className="size-4" />
      {t("settings.labels.add")}
    </Btn>
  );

  return (
    <Card style={listCard(COLS)}>
      <CardHead
        title={t(`settings.labels.${scope}.title`)}
        sub={t(`settings.labels.${scope}.desc`)}
        actions={
          canEdit ? (
            <>
              {addBtn}
              <Btn small tone="dark" onClick={() => void save()} disabled={state === "saving"}>
                {state === "saving" ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
                {t("common.save")}
              </Btn>
            </>
          ) : null
        }
      />

      {rows.length === 0 ? (
        <EmptyState
          icon={Tags}
          title={t("settings.labels.emptyTitle")}
          body={t("settings.labels.empty")}
          action={canEdit ? addBtn : undefined}
        />
      ) : (
        <>
          <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 22 }}>
            <span>{t("settings.labels.col.name")}</span>
            <span>{t("settings.labels.col.tone")}</span>
            <span>{t("settings.labels.col.preview")}</span>
            <span />
          </div>

          <div className="crm-rows pt-0">
            {rows.map((row) => (
              <LabelRow
                key={row.key}
                row={row}
                canEdit={canEdit}
                onPatch={(next) => patch(row.key, next)}
                onRemove={() => setRows((prev) => prev.filter((r) => r.key !== row.key))}
              />
            ))}
          </div>
        </>
      )}

      <div className="crm-tfoot">
        {/* `used`, not `n`: `n` is the catalogue's plural selector, and this
            string is a ratio that reads the same at every count. */}
        {canEdit ? t("settings.labels.limit", { used: rows.length, max: MAX_LABELS }) : t("settings.labels.readOnly")}
        {state === "saved" ? <span className="crm-push text-[var(--color-success)]">{t("common.saved")}</span> : null}
        {state === "error" ? <span className="crm-push text-[var(--color-destructive)]">{t("common.error")}</span> : null}
      </div>
    </Card>
  );
}

export function LabelSettings({ user }: { user: StaffUser }) {
  const t = useT();
  const [sets, setSets] = useState<CrmLabelSets | null>(null);
  const [failed, setFailed] = useState(false);
  const [nonce, setNonce] = useState(0);

  /**
   * A failed read must NOT fall through to an empty form.
   *
   * The desks swallow this failure and show no labels, which is harmless there
   * because they only read. Here the same silence would be data loss: an empty
   * form next to a live Save button, and one click writes `[]` over a registry
   * of eight labels. Worse, the server only falls back to the built-in defaults
   * when the Setting row is ABSENT - once an empty array is stored, the
   * fallback is gone for good and every chip on every thread disappears.
   *
   * So the failure gets its own state. Nothing is editable until the real list
   * is on screen.
   */
  useEffect(() => {
    let alive = true;
    setFailed(false);
    getCrmLabels()
      .then((res) => alive && setSets(res))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [nonce]);

  if (failed) {
    return (
      <Card>
        <EmptyState
          icon={AlertTriangle}
          title={t("settings.labels.loadFailedTitle")}
          body={t("settings.labels.loadFailed")}
          action={
            <Btn onClick={() => setNonce((n) => n + 1)}>
              <RefreshCw className="size-4" />
              {t("error.pane.retry")}
            </Btn>
          }
        />
      </Card>
    );
  }

  if (!sets) {
    return (
      <div className="flex justify-center py-20 text-[var(--color-muted-foreground)]">
        <Loader2 className="size-6 animate-spin" />
      </div>
    );
  }

  return (
    // Both lists share one open slot: without a host the row menus and the
    // colour pickers are dead buttons that log nothing.
    <PopHost>
      <div className="space-y-4">
        <p className="crm-sub !mt-0">{t("settings.labels.intro")}</p>
        <LabelList
          scope="mail"
          labels={sets.mail}
          canEdit={userCanPlatform(user, "manage:crm.email")}
          onSaved={(mail) => setSets((s) => (s ? { ...s, mail } : s))}
        />
        <LabelList
          scope="calls"
          labels={sets.calls}
          canEdit={userCanPlatform(user, "manage:crm.calls")}
          onSaved={(calls) => setSets((s) => (s ? { ...s, calls } : s))}
        />
      </div>
    </PopHost>
  );
}
